import { useState } from 'react';

import { AlertBox, Link } from '@iwoca/orion';
import { clsx } from 'clsx';

import { iwocapayTelDisplay } from '../../../../constants.json';
import downVector from '../../assets/downVector.svg';
import exclamationTriangle from '../../assets/exclamationTriangle.svg';
import stepOneSVG from '../../assets/stepOne.svg';
import stepTwoSVG from '../../assets/stepTwo.svg';
import { useGetIntegrationStatus } from '../../hooks/useGetIntegrationStatus.hook';
import { Magento } from '../../Integrations';
import { IntegrationTickAlert } from '../IntegrationTickAlert/IntegrationTickAlert';

export const MagentoStep1 = () => {
  const { integrationStatus } = useGetIntegrationStatus({
    integrationId: Magento.id,
  });

  const isStepComplete =
    integrationStatus &&
    ['GENERATED_KEYS', 'CONNECTED'].includes(integrationStatus);

  return (
    <div className="grid grid-cols-[6rem_1fr] grid-rows-[auto_1fr] pb-l pl-0 pr-r pt-l max-600:grid-rows-[auto]">
      <div className="row-span-2 row-start-1 flex flex-col justify-start max-600:row-span-1 max-600:flex">
        <img
          src={stepOneSVG}
          alt="Step One"
          className={clsx('self-center', { 'opacity-50': isStepComplete })}
        />
      </div>
      <div className="flex justify-between max-600:flex-col max-600:content-start">
        <div
          className={clsx(
            'm-0 flex flex-wrap justify-start pt-xs text-l font-med max-600:my-[0.5rem] max-600:flex-col max-600:pt-0',
            { 'opacity-50': isStepComplete },
          )}
        >
          <div className="m-0 whitespace-nowrap text-l font-med">Step 1</div>
          <div className="m-0 px-s font-med max-600:hidden">-</div>
          <div>We'll be in touch soon</div>
        </div>
        {isStepComplete && <IntegrationTickAlert text="Step complete" />}
      </div>
      <div className="pt-xl max-600:col-span-2 max-600:col-start-1 max-600:pt-[0.5rem]">
        <p
          className={clsx('m-0 p-0 text-s font-reg', {
            'opacity-50 max-600:hidden': isStepComplete,
          })}
        >
          Your account manager will run through the process, answer any
          questions and kick things off. Something urgent? You can contact us
          now on {iwocapayTelDisplay}.
        </p>
      </div>
    </div>
  );
};

export const MagentoStep2 = () => {
  const [showBody, setShowBody] = useState(false);
  const handleClick = () => {
    setShowBody(!showBody);
  };

  const { integrationStatus } = useGetIntegrationStatus({
    integrationId: Magento.id,
  });

  const isStepComplete = integrationStatus && integrationStatus === 'CONNECTED';

  const isActiveStep =
    integrationStatus && ['GENERATED_KEYS'].includes(integrationStatus);

  return (
    <div className="grid grid-cols-[6rem_1fr] grid-rows-[auto_1fr] pb-l pl-0 pr-r pt-l max-600:grid-rows-[auto]">
      <div className="row-span-2 row-start-1 flex flex-col justify-start max-600:row-span-1 max-600:flex">
        <img
          src={stepTwoSVG}
          alt="Step Two"
          className={clsx('self-center', { 'opacity-50': isStepComplete })}
        />
      </div>
      <div className="flex justify-between max-600:flex-col max-600:content-start">
        <div
          className={clsx(
            'm-0 flex flex-wrap justify-start pt-xs text-l font-med max-600:my-[0.5rem] max-600:flex-col max-600:pt-0',
            { 'opacity-50': isStepComplete },
          )}
        >
          <div className="m-0 whitespace-nowrap text-l font-med">Step 2</div>
          <div className="m-0 px-s font-med max-600:hidden">-</div>
          <div>Add iwocaPay to your {Magento.company_name} store</div>
        </div>
        {isStepComplete && <IntegrationTickAlert text="Step complete" />}
      </div>
      <div className="pt-xl max-600:col-span-2 max-600:col-start-1 max-600:pt-[0.5rem]">
        {!isActiveStep && !isStepComplete && (
          <button
            className={clsx(
              'cursor-pointer border-none bg-none p-0 text-s font-bold text-primary-40',
              { 'mb-xl': showBody },
            )}
            onClick={handleClick}
          >
            {showBody
              ? 'Close install walkthrough'
              : 'Go to install walkthrough'}
            <img
              src={downVector}
              alt="Down Arrow"
              className={clsx('px-s', { 'rotate-180 pt-[0.15rem]': showBody })}
            />
          </button>
        )}
        {(isActiveStep || showBody) && <MagentoStep3Body />}
      </div>
    </div>
  );
};

const MagentoStep3Body = () => {
  return (
    <div className="m-0 p-0 text-s font-reg">
      <AlertBox
        variant="failure"
        className="flex items-start justify-start gap-[0.7rem] text-s font-reg"
      >
        <img src={exclamationTriangle} alt="Warning Icon" />
        These are technical instructions and you might need a developer. You
        should back up your database before making changes to Magento, and run
        the instructions in maintenance mode.
      </AlertBox>
      <div className="h-l" />
      <div className="text-m font-bold">Installation instructions</div>
      <p>
        Follow our detailed Magento integration guide:{' '}
        <Link
          target="_blank"
          href={
            'https://ecommerce-api.iwoca.com/guides/plugin-solution/magento/#installing-iwocapay-in-magento-2'
          }
          className="inline-flex items-center gap-xs text-left !text-s font-bold !text-primary-40 no-underline hover:underline"
        >
          Installing iwocaPay in Magento 2
        </Link>
      </p>
    </div>
  );
};
